import { loadStripe } from '@stripe/stripe-js'
import { useModal } from '@wrisk/ui-components'
import React, { useCallback } from 'react'

import {
  UnauthorizedModalContent,
  useAdminUnauthorised,
  usePrincipal,
} from '../application/authentication'
import {
  AdminSettleModalContent,
  AdminSettleModalFormProps,
} from '../application/organisms/admin'
import { usePolicy } from '../application/product/policy/policyContext'
import { getPolicyholder } from '../domain'
import { useWriskTranslation } from '../infrastructure/internationalisation'
import { useApiErrorHandlingAsyncCallback } from './auth'
import { useMotoCheckoutRequest, useStripeCheckoutRequest } from './stripeCheckoutRequest'

const {
  stripe: { apiKey, stripeAccount },
} = CONFIG

interface StripeMtaAdmin {
  adjustmentId: string
  onSuccess: () => void
}

interface StripeMtaUser {
  adjustmentId: string
  successPath: string
  cancelPath: string
}

export const useStripeMtaUser = ({
  adjustmentId,
  successPath,
  cancelPath,
}: StripeMtaUser) => {
  const { apiClient } = usePrincipal()

  const adminUnauthorised = useAdminUnauthorised()

  const request = useStripeCheckoutRequest({
    saveCard: false,
    successPath,
    cancelPath,
  })

  return useApiErrorHandlingAsyncCallback(
    adminUnauthorised(async () => {
      const stripe = await loadStripe(apiKey, { stripeAccount })

      await stripe?.redirectToCheckout(
        await apiClient.stripeCheckoutMta(adjustmentId, request),
      )
    }),
  )
}

export const useStripeMtaAdmin = ({ adjustmentId, onSuccess }: StripeMtaAdmin) => {
  const { show } = useModal()
  const { i18n, t } = useWriskTranslation()
  const { apiClient, isAdmin } = usePrincipal()

  const { policy } = usePolicy()

  const { quote } = policy.currentPolicyVersion

  const {
    data: { dateOfBirth },
  } = getPolicyholder(quote)

  const request = useMotoCheckoutRequest({
    saveCard: false,
  })

  const invokeApi = useCallback(
    async (props: AdminSettleModalFormProps) =>
      await apiClient.stripeMotoCheckoutMta(adjustmentId, {
        ...request,
        ...props,
      }),
    [apiClient, i18n.language, t],
  )

  return useApiErrorHandlingAsyncCallback(async () => {
    show({
      content: isAdmin ? (
        <AdminSettleModalContent
          invokeApi={invokeApi}
          dateOfBirth={dateOfBirth}
          onSuccess={onSuccess}
        />
      ) : (
        <UnauthorizedModalContent />
      ),
    })
  })
}
